import { useEffect, useState } from "react";
import { Sparkles, ShieldAlert, MessageCircle, Activity, Bell, RefreshCw, X, Check, Building2, User as UserIcon } from "lucide-react";

const BASE = import.meta.env.BASE_URL;

const sevColor: any = {
  routine: "text-white/55 bg-white/5 border-white/10",
  monitoring: "text-blue-300 bg-blue-500/10 border-blue-500/30",
  attention: "text-amber-300 bg-amber-500/10 border-amber-500/30",
  critical: "text-red-300 bg-red-500/10 border-red-500/30",
};

const reasonLabel: Record<string, string> = {
  stale_critical: "Stale critical task",
  high_overreaction: "Pattern of overreaction",
  no_artifacts: "Company gone dark",
  loop_unclosed: "Severity-graded artifact, no follow-through",
};

export default function AdminViren() {
  const [briefs, setBriefs] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<"all" | "to_banker" | "to_meera">("all");
  const [escalations, setEscalations] = useState<any[]>([]);
  const [escLoading, setEscLoading] = useState(true);
  const [scanning, setScanning] = useState(false);
  const [scanMsg, setScanMsg] = useState<string | null>(null);

  const [escError, setEscError] = useState<string | null>(null);

  const reloadEscalations = () => {
    const token = localStorage.getItem("admin_token") || "";
    setEscLoading(true); setEscError(null);
    fetch(`${BASE}api/admin/viren/escalations?status=open`, { headers: { "x-admin-token": token } })
      .then(async r => {
        if (r.status === 401 || r.status === 403) { setEscError("Admin session expired. Sign in again."); setEscLoading(false); return; }
        if (!r.ok) { setEscError(`Couldn't load escalations (HTTP ${r.status}).`); setEscLoading(false); return; }
        const data = await r.json(); setEscalations(data); setEscLoading(false);
      })
      .catch(() => { setEscError("Network error loading escalations."); setEscLoading(false); });
  };

  const runScan = async () => {
    const token = localStorage.getItem("admin_token") || "";
    setScanning(true); setScanMsg(null);
    try {
      const r = await fetch(`${BASE}api/admin/viren/run-escalations`, { method: "POST", headers: { "x-admin-token": token } });
      if (!r.ok) { setScanMsg(r.status === 401 || r.status === 403 ? "Not authorized — sign in again." : `Scan failed (HTTP ${r.status}).`); }
      else {
        const d = await r.json();
        setScanMsg(`Scan complete — ${d.created || 0} new, ${d.skipped || 0} already open.`);
        reloadEscalations();
      }
    } catch { setScanMsg("Scan failed — network error."); }
    setScanning(false);
  };

  const updateEscalation = async (id: number, status: "dismissed" | "resolved") => {
    const token = localStorage.getItem("admin_token") || "";
    try {
      const r = await fetch(`${BASE}api/admin/viren/escalations/${id}`, {
        method: "PATCH",
        headers: { "x-admin-token": token, "Content-Type": "application/json" },
        body: JSON.stringify({ status }),
      });
      if (!r.ok) { setEscError(`Couldn't update (HTTP ${r.status}). Refresh and try again.`); return; }
      setEscalations(prev => prev.filter(e => e.id !== id));
    } catch { setEscError("Network error updating escalation."); }
  };

  useEffect(() => {
    const token = localStorage.getItem("admin_token") || "";
    fetch(`${BASE}api/admin/viren/briefs`, { headers: { "x-admin-token": token } })
      .then(r => r.ok ? r.json() : [])
      .then(data => { setBriefs(data); setLoading(false); })
      .catch(() => setLoading(false));
    reloadEscalations();
  }, []);

  const filtered = briefs.filter(b => filter === "all" ? true : b.direction === filter);
  const toMeera = briefs.filter(b => b.direction === "to_meera");
  const overreactions = toMeera.filter(b => b.bankerOverreaction === true).length;
  const critical = toMeera.filter(b => b.severity === "critical").length;

  return (
    <div className="bg-[#0A0A0A] border border-white/[0.08] rounded-2xl p-6 md:p-8 space-y-5 -mx-2 md:mx-0">
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-xl bg-purple-500/20 border border-purple-500/30 flex items-center justify-center"><Sparkles className="w-5 h-5 text-purple-300" /></div>
        <div>
          <h1 className="text-3xl font-bold text-white">Viren · Senior Banking Lead</h1>
          <p className="text-white/55 text-sm mt-1">The mature voice between Meera and the bankers. Preps sharp founder questions for your team and translates banker overreactions into measured signals for Meera.</p>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <Stat label="Total briefs" value={briefs.length.toString()} />
        <Stat label="Question prep sessions" value={briefs.filter(b => b.direction === "to_banker").length.toString()} />
        <Stat label="Overreactions reframed" value={overreactions.toString()} accent={overreactions > 0 ? "amber" : undefined} />
        <Stat label="Marked critical" value={critical.toString()} accent={critical > 0 ? "red" : undefined} />
        <Stat label="Open escalations" value={escalations.length.toString()} accent={escalations.length > 0 ? "amber" : undefined} />
      </div>

      {/* Escalations: Viren complaining about banker performance */}
      <div className="bg-amber-500/[0.04] border border-amber-500/20 rounded-2xl p-5">
        <div className="flex items-start justify-between gap-3 mb-3 flex-wrap">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-lg bg-amber-500/15 border border-amber-500/30 flex items-center justify-center"><Bell className="w-4 h-4 text-amber-300" /></div>
            <div>
              <h2 className="text-lg font-semibold text-white">Viren's escalations to you</h2>
              <p className="text-white/55 text-xs">Patterns Viren has noticed across the banker queue. Auto-runs daily at 9 AM IST.</p>
            </div>
          </div>
          <button onClick={runScan} disabled={scanning}
            className="px-3 py-1.5 rounded-lg bg-amber-500/15 border border-amber-500/40 text-amber-200 text-xs font-medium inline-flex items-center gap-1.5 hover:bg-amber-500/25 disabled:opacity-50">
            <RefreshCw className={`w-3.5 h-3.5 ${scanning ? "animate-spin" : ""}`} /> {scanning ? "Scanning queue…" : "Run scan now"}
          </button>
        </div>
        {scanMsg && <p className="text-amber-200/80 text-xs mb-3">{scanMsg}</p>}
        {escError && <div className="bg-red-500/10 border border-red-500/30 rounded-lg px-3 py-2 mb-3 text-red-200 text-xs">{escError}</div>}
        {escLoading ? (
          <div className="space-y-2">{[...Array(2)].map((_, i) => <div key={i} className="h-20 bg-white/[0.04] rounded-lg animate-pulse" />)}</div>
        ) : escalations.length === 0 ? (
          <div className="bg-white/[0.04] border border-white/[0.1] rounded-xl p-8 text-center">
            <Check className="w-8 h-8 text-emerald-400/60 mx-auto mb-2" />
            <p className="text-white/65 text-sm">All clear. Viren has nothing to flag.</p>
            <p className="text-white/40 text-xs mt-1">He'll wake up tomorrow at 9 AM and look again.</p>
          </div>
        ) : (
          <div className="space-y-2.5">
            {escalations.map(e => <EscalationCard key={e.id} esc={e} onUpdate={updateEscalation} />)}
          </div>
        )}
      </div>

      <div className="flex items-center gap-2">
        {([
          { k: "all", label: "All" },
          { k: "to_banker", label: "Question prep (→ Banker)" },
          { k: "to_meera", label: "Artifact digests (→ Meera)" },
        ] as const).map(opt => (
          <button key={opt.k} onClick={() => setFilter(opt.k as any)}
            className={`px-3 py-1.5 rounded-full text-xs font-medium border ${filter === opt.k ? "bg-purple-500/20 border-purple-500/40 text-purple-200" : "bg-white/[0.04] border-white/[0.1] text-white/60 hover:border-white/[0.2]"}`}>
            {opt.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="space-y-2">{[...Array(3)].map((_, i) => <div key={i} className="h-24 bg-white/[0.04] rounded-lg animate-pulse" />)}</div>
      ) : filtered.length === 0 ? (
        <div className="bg-white/[0.04] border border-white/[0.1] rounded-xl p-12 text-center">
          <Sparkles className="w-10 h-10 text-white/20 mx-auto mb-2" />
          <p className="text-white/55 text-sm">No briefs yet. Viren wakes up when bankers prep for a call or upload an artifact.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map(b => <BriefCard key={b.id} brief={b} />)}
        </div>
      )}
    </div>
  );
}

function EscalationCard({ esc, onUpdate }: { esc: any; onUpdate: (id: number, status: "dismissed" | "resolved") => void }) {
  return (
    <div className="bg-white/[0.04] border border-white/[0.1] rounded-xl p-4">
      <div className="flex items-start justify-between gap-3 mb-2 flex-wrap">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-[10px] uppercase tracking-wider font-mono px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-300 border border-amber-500/30">{reasonLabel[esc.escalationReason] || esc.escalationReason}</span>
          {esc.severity && <span className={`text-[10px] uppercase tracking-wider font-mono px-1.5 py-0.5 rounded border ${sevColor[esc.severity]}`}>{esc.severity}</span>}
          {esc.banker && <span className="text-[11px] text-white/70 inline-flex items-center gap-1"><UserIcon className="w-3 h-3" /> {esc.banker.name}</span>}
          {esc.company && <span className="text-[11px] text-white/70 inline-flex items-center gap-1"><Building2 className="w-3 h-3" /> {esc.company.name}</span>}
        </div>
        <p className="text-white/35 text-[10px] font-mono">{new Date(esc.createdAt).toLocaleString()}</p>
      </div>
      {esc.rationale && (
        <p className="text-white/85 text-sm leading-relaxed mb-2">{esc.rationale}</p>
      )}
      {esc.meeraUpdate && (
        <div className="bg-amber-500/10 border-l-2 border-amber-400 pl-3 py-1.5 rounded-r mb-2">
          <p className="text-[10px] uppercase tracking-wider text-amber-300/70 font-mono mb-0.5">Suggested next step</p>
          <p className="text-amber-100 text-xs leading-relaxed">{esc.meeraUpdate}</p>
        </div>
      )}
      <div className="flex items-center gap-2 mt-2">
        <button onClick={() => onUpdate(esc.id, "resolved")}
          className="px-2.5 py-1 rounded-md bg-emerald-500/15 border border-emerald-500/30 text-emerald-200 text-[11px] font-medium inline-flex items-center gap-1 hover:bg-emerald-500/25">
          <Check className="w-3 h-3" /> Acted on
        </button>
        <button onClick={() => onUpdate(esc.id, "dismissed")}
          className="px-2.5 py-1 rounded-md bg-white/[0.04] border border-white/[0.15] text-white/60 text-[11px] font-medium inline-flex items-center gap-1 hover:bg-white/[0.08]">
          <X className="w-3 h-3" /> Dismiss
        </button>
        {esc.taskId && (
          <span className="px-2.5 py-1 rounded-md bg-white/[0.04] border border-white/[0.1] text-white/45 text-[11px] font-mono">task #{esc.taskId}</span>
        )}
      </div>
    </div>
  );
}

function Stat({ label, value, accent }: { label: string; value: string; accent?: "amber" | "red" }) {
  const tone = accent === "red" ? "text-red-300" : accent === "amber" ? "text-amber-300" : "text-white";
  return (
    <div className="bg-white/[0.04] border border-white/[0.08] rounded-xl p-3.5">
      <p className="text-[10px] uppercase tracking-wider text-white/50 font-mono">{label}</p>
      <p className={`text-2xl font-bold mt-1 ${tone}`}>{value}</p>
    </div>
  );
}

function BriefCard({ brief }: { brief: any }) {
  const isPrep = brief.direction === "to_banker";
  const questions = (() => { try { return JSON.parse(brief.questionsJson || "[]"); } catch { return []; } })();
  return (
    <div className="bg-white/[0.04] border border-white/[0.08] rounded-xl p-4">
      <div className="flex items-start justify-between gap-3 mb-2 flex-wrap">
        <div className="flex items-center gap-2">
          {isPrep
            ? <span className="text-[10px] uppercase tracking-wider font-mono px-1.5 py-0.5 rounded bg-purple-500/15 text-purple-300 border border-purple-500/30 inline-flex items-center gap-1"><MessageCircle className="w-3 h-3" /> Prep → Banker</span>
            : <span className="text-[10px] uppercase tracking-wider font-mono px-1.5 py-0.5 rounded bg-emerald-500/15 text-emerald-300 border border-emerald-500/30 inline-flex items-center gap-1"><Activity className="w-3 h-3" /> Digest → Meera</span>}
          {brief.severity && !isPrep && <span className={`text-[10px] uppercase tracking-wider font-mono px-1.5 py-0.5 rounded border ${sevColor[brief.severity]}`}>{brief.severity}</span>}
          {brief.bankerOverreaction === true && <span className="text-[10px] uppercase tracking-wider font-mono px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-300 border border-amber-500/30 inline-flex items-center gap-1"><ShieldAlert className="w-3 h-3" /> reframed</span>}
        </div>
        <p className="text-white/35 text-[10px] font-mono">{new Date(brief.createdAt).toLocaleString()}</p>
      </div>
      {brief.inputSummary && <p className="text-white/65 text-xs mb-2">{brief.inputSummary}</p>}
      {isPrep ? (
        <div className="space-y-1.5">
          {brief.rationale && <p className="text-white/75 text-xs italic border-l-2 border-purple-500/30 pl-2">{brief.rationale}</p>}
          {questions.length > 0 && (
            <ul className="space-y-1 mt-2">
              {questions.slice(0, 5).map((q: any, i: number) => (
                <li key={i} className="text-white/80 text-xs flex gap-2"><span className="text-purple-300 font-mono">{i + 1}.</span>{q.question}</li>
              ))}
              {questions.length > 5 && <li className="text-white/45 text-xs italic">+ {questions.length - 5} more</li>}
            </ul>
          )}
        </div>
      ) : (
        <div className="space-y-2">
          {brief.founderResponseDraft && (
            <div>
              <p className="text-white/45 text-[10px] uppercase tracking-wider font-mono mb-0.5">Founder-facing draft</p>
              <p className="text-white/85 text-xs leading-relaxed">{brief.founderResponseDraft}</p>
            </div>
          )}
          {brief.meeraUpdate && (
            <div>
              <p className="text-white/45 text-[10px] uppercase tracking-wider font-mono mb-0.5">Calm note for Meera</p>
              <p className="text-white/70 text-xs italic border-l border-emerald-500/30 pl-2">{brief.meeraUpdate}</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
